import React from 'react';

/*
 * The ticket info-text subset, rendered as React elements.
 *
 * The grammar here is a copy of the backend's src/services/ticketInfoText.js,
 * which renders the same tokens to an HTML string for the receipt, and of the
 * admin's components/Tickets/infoText.js, which renders the venue's preview.
 * The three repositories share no history and cannot import from one another,
 * so the parser is duplicated on purpose — change one and change all three in
 * the same breath, or a venue's bullet list will appear in one place and not
 * the others.
 *
 *     **bold**    *italic*    - bullet    blank line = new paragraph
 *
 * This renderer builds ELEMENTS, never markup. There is no
 * dangerouslySetInnerHTML anywhere in it, so even if the grammar were wrong the
 * worst a venue could produce is text that looks odd — not a tag. React escapes
 * the strings for us, which is the same structural guarantee the backend gets
 * by escaping before it adds a tag.
 */

export const MAX_INFO_TEXT = 2000;

const inline = (text) => {
    const out = [];
    let buffer = '';
    let i = 0;

    const flush = () => {
        if (buffer) {
            out.push({ text: buffer });
            buffer = '';
        }
    };

    while (i < text.length) {
        if (text.slice(i, i + 2) === '**') {
            const end = text.indexOf('**', i + 2);
            if (end > i + 2) {
                flush();
                out.push({ text: text.slice(i + 2, end), bold: true });
                i = end + 2;
                continue;
            }
        }

        if (text[i] === '*') {
            const end = text.indexOf('*', i + 1);
            if (end > i + 1 && text.slice(i + 1, end).indexOf('\n') === -1) {
                flush();
                out.push({ text: text.slice(i + 1, end), italic: true });
                i = end + 1;
                continue;
            }
        }

        buffer += text[i];
        i += 1;
    }

    flush();
    return out;
};

export const parseInfoText = (raw) => {
    const text = String(raw == null ? '' : raw)
        .replace(/\r\n?/g, '\n')
        .slice(0, MAX_INFO_TEXT);
    if (!text.trim()) return [];

    const blocks = [];
    const lines = text.split('\n');
    let paragraph = [];
    let list = null;

    const closeParagraph = () => {
        if (paragraph.length) {
            blocks.push({ type: 'p', spans: inline(paragraph.join('\n')) });
            paragraph = [];
        }
    };
    const closeList = () => {
        if (list) {
            blocks.push({ type: 'ul', items: list });
            list = null;
        }
    };

    for (const line of lines) {
        const bullet = /^\s*[-*]\s+(.*)$/.exec(line);

        if (bullet) {
            closeParagraph();
            if (!list) list = [];
            list.push(inline(bullet[1]));
            continue;
        }

        if (!line.trim()) {
            closeParagraph();
            closeList();
            continue;
        }

        closeList();
        paragraph.push(line);
    }

    closeParagraph();
    closeList();
    return blocks;
};

// A span's own line breaks are kept: a venue pressing return once inside a
// paragraph means a line break, and swallowing it would silently reflow their
// address block into one line.
const spans = (list, keyPrefix) =>
    list.map((span, i) => {
        const key = `${keyPrefix}-${i}`;
        const parts = String(span.text).split('\n');
        const content = parts.map((part, j) => (
            <React.Fragment key={`${key}-${j}`}>
                {j > 0 ? <br /> : null}
                {part}
            </React.Fragment>
        ));

        if (span.bold) return <strong key={key}>{content}</strong>;
        if (span.italic) return <em key={key}>{content}</em>;
        return <React.Fragment key={key}>{content}</React.Fragment>;
    });

export const renderInfoText = (raw) => {
    const blocks = parseInfoText(raw);
    if (blocks.length === 0) return null;

    return (
        <div className="tk-richtext">
            {blocks.map((block, i) =>
                block.type === 'ul' ? (
                    <ul key={`b-${i}`}>
                        {block.items.map((item, j) => (
                            <li key={`b-${i}-${j}`}>{spans(item, `b-${i}-${j}`)}</li>
                        ))}
                    </ul>
                ) : (
                    <p key={`b-${i}`}>{spans(block.spans, `b-${i}`)}</p>
                ),
            )}
        </div>
    );
};

export default renderInfoText;
